import { API_ENDPOINT } from '@/constant/constant.ts'

/**
 * - ok: 새 Access Token 을 받았다.
 * - rejected: 서버가 갱신을 거절했다(리프레시 쿠키가 없거나 만료·무효). 세션이 끝난 것이다.
 * - unavailable: 지금은 갱신하지 못했다(429·408·5xx·네트워크 오류). 세션은 그대로이고 나중에 다시 시도하면 된다.
 */
export type RefreshOutcome =
    | { status: 'ok'; token: string }
    | { status: 'rejected' }
    | {
          status: 'unavailable'
          reason: 'rate_limited' | 'server_error' | 'network'
          /** 서버가 `Retry-After` 로 알려 준 대기 시간. 없으면 0 */
          retryAfterMs: number
      }

let activeRefresh: Promise<RefreshOutcome> | null = null
let cooldown: { until: number; outcome: RefreshOutcome } | null = null
const FAILURE_COOLDOWN_MS = 5000
const MAX_RETRY_AFTER_MS = 60_000

const parseRetryAfterMs = (value: string | null | undefined): number => {
    const seconds = value == null ? NaN : Number(value)
    return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds * 1000, MAX_RETRY_AFTER_MS) : 0
}

const requestRefresh = async (): Promise<RefreshOutcome> => {
    try {
        const response = await fetch(`${API_ENDPOINT}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        })

        if (!response.ok) {
            if (response.status === 429) {
                return {
                    status: 'unavailable',
                    reason: 'rate_limited',
                    retryAfterMs: parseRetryAfterMs(response.headers?.get('Retry-After'))
                }
            }
            if (response.status >= 400 && response.status < 500 && response.status !== 408) {
                return { status: 'rejected' }
            }
            return { status: 'unavailable', reason: 'server_error', retryAfterMs: 0 }
        }

        const resData = await response.json()
        const token = resData.data?.accessToken || resData.data?.token
        return token
            ? { status: 'ok', token }
            : { status: 'unavailable', reason: 'server_error', retryAfterMs: 0 }
    } catch {
        return { status: 'unavailable', reason: 'network', retryAfterMs: 0 }
    }
}

/**
 * HttpOnly Refresh Token 쿠키를 사용하여 새로운 Access Token을 발급받음
 * api.ts와의 순환 참조를 피하기 위해 raw fetch를 사용함
 * 실패하면 잠시 같은 결과를 돌려줘 요청이 몰리지 않게 한다. 429 는 `Retry-After` 만큼 늘린다.
 */
export const apiRefreshOutcome = async (
    options: { bypassCooldown?: boolean } = {}
): Promise<RefreshOutcome> => {
    if (activeRefresh) return activeRefresh

    if (!options.bypassCooldown && cooldown && Date.now() < cooldown.until) {
        const { outcome, until } = cooldown
        return outcome.status === 'unavailable'
            ? { ...outcome, retryAfterMs: Math.max(outcome.retryAfterMs, until - Date.now()) }
            : outcome
    }

    activeRefresh = requestRefresh().then((outcome) => {
        if (outcome.status === 'ok') {
            cooldown = null
        } else {
            const waitMs =
                outcome.status === 'unavailable'
                    ? Math.max(FAILURE_COOLDOWN_MS, outcome.retryAfterMs)
                    : FAILURE_COOLDOWN_MS
            cooldown = { until: Date.now() + waitMs, outcome }
        }
        return outcome
    })

    try {
        return await activeRefresh
    } finally {
        activeRefresh = null
    }
}

/** 새 Access Token 혹은 실패 시 null. 실패 종류가 필요하면 `apiRefreshOutcome` 을 쓴다. */
export const apiRefresh = async (): Promise<string | null> => {
    const outcome = await apiRefreshOutcome()
    return outcome.status === 'ok' ? outcome.token : null
}

export const resetRefreshStateForTests = () => {
    activeRefresh = null
    cooldown = null
}
